import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["style"];
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import throttle from 'hs-lodash/throttle';
import { CANDY_APPLE, KOALA, LORAX, OBSIDIAN, OLAF, PANTERA } from 'HubStyleTokens/colors';
import FormattedFraction from 'I18n/components/FormattedFraction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import * as KeyCodes from 'UIComponents/constants/KeyCodes';
import UIBox from 'UIComponents/layout/UIBox';
import Big from 'UIComponents/elements/Big';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIImage from 'UIComponents/image/UIImage';
import UIModal from 'UIComponents/dialog/UIModal';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UIOverhang from 'UIComponents/scroll/UIOverhang';
import UIOverlay from 'UIComponents/overlay/UIOverlay';
import UIScrollingColumn from 'UIComponents/layout/UIScrollingColumn';
import { useResponsiveProps } from '../../hooks/useResponsiveProps';
import { requestIdleCallback } from '../../utils/requestIdleCallback';
import * as ScreenSizes from '../../layout/constants/ScreenSizes';
import MediaQuery from '../../layout/components/MediaQuery';
import ScreenshotsGrid from './ScreenshotsGrid';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useFocusCloseButton } from '../../hooks/useFocusCloseButton';
import FullscreenModalHeader from './FullscreenModalHeader';
import FullscreenModalHeaderGroup from './FullscreenModalHeaderGroup';
import FullscreenModalHeading from './FullscreenModalHeading';
import ScreenshotsModalShareButton from './ScreenshotsModalShareButton';
import { usePrevious } from '../../hooks/usePrevious';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROGRESS_HEIGHT = 6;
const SCROLL_THRESHOLD = 100;
const SUBSCREEN = 'screenshots modal';
const CAROUSEL_SUBSCREEN = 'screenshots modal carousel';
const NoopOverhang = () => null;
const ProgressWrapper = styled.div.withConfig({
  displayName: "ScreenshotsModal__ProgressWrapper",
  componentId: "sc-1ve06gk-0"
})(["position:relative;height:", "px;background-color:", ";"], PROGRESS_HEIGHT, KOALA);
const CarouselOverlay = styled(UIOverlay).withConfig({
  displayName: "ScreenshotsModal__CarouselOverlay",
  componentId: "sc-1ve06gk-1"
})(["&&{padding-top:0;padding-bottom:0;background-color:", ";}"], PANTERA);
const CarouselBody = styled(props => /*#__PURE__*/_jsx(UIFlex, Object.assign({}, props, {
  align: "stretch",
  justify: "between"
}))).withConfig({
  displayName: "ScreenshotsModal__CarouselBody",
  componentId: "sc-1ve06gk-2"
})(["height:100%;overflow:auto;"]);
const CarouselStepperWrapper = props => /*#__PURE__*/_jsx(UIBox, Object.assign({}, props, {
  alignSelf: "center",
  grow: 0,
  shrink: 0
}));
const CarouselMainWrapper = styled(props => /*#__PURE__*/_jsx(UIFlex, Object.assign({}, props, {
  direction: "column",
  align: "stretch"
}))).withConfig({
  displayName: "ScreenshotsModal__CarouselMainWrapper",
  componentId: "sc-1ve06gk-3"
})(["flex-basis:100%;flex-grow:1;flex-shrink:1;overflow:auto;"]);
const CarouselImageWrapper = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(UIFlex, Object.assign({}, props, {
  ref: ref,
  justify: "center",
  style: {
    flexBasis: '100%',
    flexGrow: 1,
    flexShrink: 1,
    overflowY: 'auto'
  },
  children: props.children
})));
const makeTransparentGhostButton = Button => styled(props => /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
  use: "secondary-ghost"
}))).withConfig({
  displayName: "ScreenshotsModal__makeTransparentGhostButton",
  componentId: "sc-1ve06gk-4"
})(["border:0 !important;"]);
const CarouselCloseButton = makeTransparentGhostButton(_ref => {
  let {
      style
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsxs(UIButton, Object.assign({}, rest, {
    style: style,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "remove"
    }), /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: ScreenSizes.MD,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-common.screenshots.modal.closeButton"
      })
    })]
  }));
});
const CarouselShareButton = makeTransparentGhostButton(ScreenshotsModalShareButton);
export default function ScreenshotsModal({
  focusedImageIndex: passedFocusedImageIndex,
  ctaButton,
  name,
  onClose,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  screenshots,
  shareUrl,
  onShareClick,
  shareAnalyticsId
}) {
  const scrollElementRef = useRef(null);
  const focusedImageElementRef = useRef(null);
  const carouselImageElementRef = useRef(null);
  const carouselCloseButtonRef = useRef(null);
  const backButtonRef = useRef(null);
  const {
    closeButtonStyles,
    isMobileView,
    shareButtonStyles,
    stepButtonClassName,
    stepIconSize
  } = useResponsiveProps({
    closeButtonStyles: {
      xs: {
        textAlign: 'left',
        paddingLeft: 0
      },
      md: {
        textAlign: 'center',
        paddingLeft: ''
      }
    },
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    },
    shareButtonStyles: {
      xs: {
        textAlign: 'right',
        paddingRight: 0
      },
      md: {
        textAlign: 'center',
        paddingRight: ''
      }
    },
    stepButtonClassName: {
      xs: 'p-x-2',
      md: 'p-x-3',
      xl: 'p-x-5'
    },
    stepIconSize: {
      xs: 'extra-small',
      md: 'small',
      xl: 'default'
    }
  });
  const backButtonClassName = isMobileView ? 'p-top-2' : "";
  const titleClassName = isMobileView ? 'p-top-4' : "";
  const [focusedImageIndex, setFocusedImageIndex] = useState(passedFocusedImageIndex || 0);
  const prevFocusedImageIndex = usePrevious(focusedImageIndex);
  const hasPreviousImage = focusedImageIndex > 0;
  const hasNextImage = focusedImageIndex < screenshots.length - 1;
  const [scrollProgress, setScrollProgress] = useState(0);
  const handleScroll = throttle(evt => {
    const {
      target: scrollElement
    } = evt;
    setScrollProgress(scrollElement.scrollTop / (scrollElement.scrollHeight - scrollElement.offsetHeight));
  }, SCROLL_THRESHOLD);
  const handleScrollToFocusedImage = useCallback(behavior => {
    const cancelCallback = requestIdleCallback(() => {
      if (focusedImageElementRef.current) {
        focusedImageElementRef.current.scrollIntoView({
          behavior
        });
      }
    });
    return () => {
      cancelCallback();
    };
  }, []);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      subscreen: SUBSCREEN
    }));
  }, [onTrackInteraction]);
  const trackCarouselInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      subscreen: CAROUSEL_SUBSCREEN,
      itemIndex: focusedImageIndex
    }));
  }, [focusedImageIndex, onTrackInteraction]);
  const [carouselOpen, setCarouselOpen] = useState(passedFocusedImageIndex != null);
  const [carouselRef, setCarouselRef] = useState(null);
  const onCarouselRefChange = useCallback(node => {
    if (node !== null) {
      setCarouselRef(node._innerEl);
    }
  }, []);
  useFocusTrap(carouselOpen, carouselRef);
  const renderCarouselImageAltText = useCallback(() => {
    const {
      altText
    } = screenshots[focusedImageIndex];
    return /*#__PURE__*/_jsx(Big, {
      style: {
        color: OLAF
      },
      children: altText
    });
  }, [focusedImageIndex, screenshots]);
  const renderCarouselImage = useCallback(() => {
    const {
      altText,
      imageUrl
    } = screenshots[focusedImageIndex];
    return /*#__PURE__*/_jsx(UIImage, {
      alt: altText,
      src: imageUrl,
      tabIndex: 0
    });
  }, [focusedImageIndex, screenshots]);
  const handleCloseCarousel = useCallback(() => {
    handleScrollToFocusedImage();
    setCarouselOpen(false);
  }, [handleScrollToFocusedImage]);
  const handleCarouselPrevious = useCallback(() => {
    setFocusedImageIndex(focusedImageIndex - 1);
  }, [focusedImageIndex]);
  const handleCarouselNext = useCallback(() => {
    setFocusedImageIndex(focusedImageIndex + 1);
  }, [focusedImageIndex]);
  const handleCarouselKeydown = useCallback(evt => {
    if (evt.keyCode === KeyCodes.LEFT && hasPreviousImage) {
      handleCarouselPrevious();
    } else if (evt.keyCode === KeyCodes.RIGHT && hasNextImage) {
      handleCarouselNext();
    }
  }, [handleCarouselNext, handleCarouselPrevious, hasNextImage, hasPreviousImage]);
  useLayoutEffect(() => {
    if (scrollElementRef.current) {
      const scrollElement = scrollElementRef.current;
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
    return () => {};
  }, [handleScroll]);
  useLayoutEffect(() => {
    if (carouselOpen && focusedImageIndex !== prevFocusedImageIndex) {
      if (carouselImageElementRef.current) {
        carouselImageElementRef.current.scrollTop = 0;
      }
      handleScrollToFocusedImage('auto');
    }
  }, [focusedImageIndex, carouselOpen, prevFocusedImageIndex, handleScrollToFocusedImage]);
  useEffect(() => {
    if (carouselOpen) {
      window.addEventListener('keydown', handleCarouselKeydown);
      return () => {
        window.removeEventListener('keydown', handleCarouselKeydown);
      };
    }
    return () => {};
  }, [carouselOpen, handleCarouselKeydown]);
  useEffect(() => {
    if (carouselOpen) {
      onTrackPageview({
        subscreen: CAROUSEL_SUBSCREEN
      });
    }
  }, [carouselOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onTrackPageview({
      subscreen: SUBSCREEN
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useFocusCloseButton(!carouselOpen && !!backButtonRef.current, backButtonRef.current);
  useFocusCloseButton(carouselOpen && !!carouselCloseButtonRef.current, carouselCloseButtonRef.current);
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "fullscreen",
    children: [/*#__PURE__*/_jsxs(FullscreenModalHeader, {
      isMobileView: isMobileView,
      children: [/*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
        children: /*#__PURE__*/_jsxs(UIButton, {
          "aria-label": unescapedText('marketplace-ui-common.screenshots.modal.backButton'),
          buttonRef: backButtonRef,
          onClick: () => {
            trackInteraction('click back link');
            onClose();
          },
          style: {
            textAlign: 'left'
          },
          use: "link",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: backButtonClassName,
            name: "left",
            size: isMobileView ? 'xs' : 'xxs'
          }), /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.MD,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-common.screenshots.modal.backButton"
            })
          })]
        })
      }), /*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
        className: titleClassName,
        middle: true,
        children: [/*#__PURE__*/_jsx(FullscreenModalHeading, {
          subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-common.screenshots.modal.providerName",
            options: {
              providerName
            }
          }),
          title: name
        }), ctaButton && /*#__PURE__*/_jsx(MediaQuery, {
          maxScreenSize: ScreenSizes.MD,
          children: /*#__PURE__*/_jsx("span", {
            className: "m-top-4",
            children: ctaButton
          })
        })]
      }), /*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
        children: [shareUrl && /*#__PURE__*/_jsx(ScreenshotsModalShareButton, {
          shareUrl: shareUrl,
          onClick: onShareClick,
          analyticsId: shareAnalyticsId,
          style: shareButtonStyles,
          use: "transparent"
        }), ctaButton && /*#__PURE__*/_jsx(MediaQuery, {
          minScreenSize: ScreenSizes.MD,
          children: ctaButton
        })]
      })]
    }), /*#__PURE__*/_jsx(ProgressWrapper, {
      children: /*#__PURE__*/_jsx(UINanoProgress, {
        boxShadow: false,
        color: `linear-gradient(to right, ${CANDY_APPLE}, ${LORAX})`,
        height: PROGRESS_HEIGHT,
        value: scrollProgress * 100
      })
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "p-all-0",
      style: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(UIScrollingColumn, {
        Overhang: NoopOverhang,
        scrollElementRef: scrollElementRef,
        children: /*#__PURE__*/_jsx(ScreenshotsGrid, {
          active: !carouselOpen,
          focusedImageElementRef: focusedImageElementRef,
          focusedImageIndex: focusedImageIndex,
          onImageClick: imageIndex => {
            setFocusedImageIndex(imageIndex);
            setCarouselOpen(true);
            trackInteraction('click screenshot image', {
              itemIndex: imageIndex
            });
          },
          screenshots: screenshots
        })
      }), /*#__PURE__*/_jsx(UIOverhang, {
        side: "top",
        gradientColor: OBSIDIAN,
        opacity: 0.1,
        size: 4
      })]
    }), carouselOpen && /*#__PURE__*/_jsx(CarouselOverlay, {
      contextual: true,
      ref: onCarouselRefChange,
      use: "dark",
      width: "100%",
      children: /*#__PURE__*/_jsxs(CarouselBody, {
        children: [/*#__PURE__*/_jsx(CarouselStepperWrapper, {
          className: stepButtonClassName,
          children: /*#__PURE__*/_jsx(UIIconButton, {
            "aria-label": unescapedText('marketplace-ui-common.screenshots.modal.previous'),
            tabIndex: 0,
            disabled: !hasPreviousImage,
            onClick: () => {
              handleCarouselPrevious();
              trackCarouselInteraction('click carousel previous button');
            },
            size: stepIconSize,
            shape: "circle",
            use: "secondary-ghost",
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "left"
            })
          })
        }), /*#__PURE__*/_jsxs(CarouselMainWrapper, {
          children: [/*#__PURE__*/_jsx(UIBox, {
            basis: 0,
            grow: 1,
            children: /*#__PURE__*/_jsxs(FullscreenModalHeader, {
              className: "p-top-8 p-bottom-4 p-x-0",
              children: [/*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
                children: [/*#__PURE__*/_jsx(CarouselCloseButton, {
                  "aria-label": unescapedText('marketplace-ui-common.screenshots.modal.closeButton'),
                  buttonRef: carouselCloseButtonRef,
                  onClick: () => {
                    onClose();
                    trackCarouselInteraction('click carousel close button');
                  },
                  style: closeButtonStyles
                }), /*#__PURE__*/_jsx(MediaQuery, {
                  minScreenSize: ScreenSizes.MD,
                  children: /*#__PURE__*/_jsxs(UIButton, {
                    onClick: () => {
                      handleCloseCarousel();
                      trackCarouselInteraction('click carousel see all images button');
                    },
                    size: "sm",
                    use: "secondary-ghost",
                    children: [/*#__PURE__*/_jsx(UIIcon, {
                      name: "grid"
                    }), /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-ui-common.screenshots.modal.seeAllImages"
                    })]
                  })
                })]
              }), /*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
                middle: true,
                children: /*#__PURE__*/_jsx("span", {
                  style: {
                    color: OLAF
                  },
                  children: /*#__PURE__*/_jsx(FormattedFraction, {
                    numerator: focusedImageIndex + 1,
                    denominator: screenshots.length
                  })
                })
              }), /*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
                children: [shareUrl && /*#__PURE__*/_jsx(CarouselShareButton, {
                  "aria-label": unescapedText('marketplace-ui-common.screenshots.modal.shareButton'),
                  onClick: () => {
                    trackCarouselInteraction('click share link');
                    if (onShareClick) {
                      onShareClick();
                    }
                  },
                  responsive: true,
                  style: shareButtonStyles,
                  shareUrl: shareUrl,
                  analyticsId: shareAnalyticsId
                }), ctaButton && /*#__PURE__*/_jsx(MediaQuery, {
                  minScreenSize: ScreenSizes.MD,
                  children: ctaButton
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(UIBox, {
            basis: 0,
            grow: 1,
            children: /*#__PURE__*/_jsx("div", {
              className: "p-bottom-4 p-x-10 text-center",
              style: {
                color: OLAF
              },
              children: renderCarouselImageAltText()
            })
          }), /*#__PURE__*/_jsx(CarouselImageWrapper, {
            ref: carouselImageElementRef,
            children: renderCarouselImage()
          })]
        }), /*#__PURE__*/_jsx(CarouselStepperWrapper, {
          className: stepButtonClassName,
          children: /*#__PURE__*/_jsx(UIIconButton, {
            "aria-label": unescapedText('marketplace-ui-common.screenshots.modal.next'),
            tabIndex: 0,
            disabled: !hasNextImage,
            onClick: () => {
              handleCarouselNext();
              trackCarouselInteraction('click carousel next button');
            },
            size: stepIconSize,
            shape: "circle",
            use: "secondary-ghost",
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "right"
            })
          })
        })]
      })
    })]
  });
}